import { Box, Button, Table, TableRow, TopBar } from '@sam/components';
import { TableHeader, TableKey } from '@sam/components/src/Table/Table.types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  addOrRemove,
  downloadFile,
  Order,
  ProjectState,
  updateSchambeckUserTableConfig,
  useData,
} from 'shared';
import { generateProjectCopy, getProjectDescriptionPdf } from 'shared/src/project/Project.axios';
import { useUser } from '../../components/UserContext';
import { convertProjectsIntoTableEntries } from '../../utils/project/Project.utils';
import { IconComponent, IconType } from '../../components/icons/IconComponent';

export const ProjectOverview: React.FC = () => {
  const { axios, user, updateContextUser } = useUser();
  const { t } = useTranslation();
  const [filterValue, setFilterValue] = useState<string>('');
  const [selectedProjectStates, setSelectedProjectStates] = useState<ProjectState[]>([
    ProjectState.PROJECT_RUNNING,
  ]);
  const [selectedBusinessAreas, setSelectedBusinessAreas] = useState<{
    ids: string[];
    name: string;
  }>({ ids: [], name: '' });
  const { data: loadedProjects } = useData('PROJECT_ALL', {
    config: { fallbackData: [] },
  });
  const { data: loadedCustomers } = useData('CUSTOMER_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });
  const { data: loadedCustomerLocations } = useData('CUSTOMER_LOCATION_ALL', {
    config: { fallbackData: [] },
  });
  const { data: loadedCustomerUsers } = useData('CUSTOMER_USERS_ALL', {
    config: { fallbackData: [] },
  });
  const { data: loadedOrders } = useData('ORDER_ALL', {
    config: { fallbackData: [] },
  });
  const { data: loadedBusinessAreas } = useData('BUSINESSAREA_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });

  const navigate = useNavigate();

  /**
   * Helper method to download a preojectDescription
   */
  const downloadDescription = useCallback((projectId: string): void => {
    getProjectDescriptionPdf(axios, projectId).then(
      (loadedFile) =>
        loadedFile && downloadFile(loadedFile, t('pages.project.overview.descriptionFileName')),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Holds the options for the business areas
   * multiple areas with the same name are getting grouped
   */
  const businessAreaOptions: { ids: string[]; name: string }[] = useMemo((): {
    ids: string[];
    name: string;
  }[] => {
    let options: { ids: string[]; name: string }[] = [];
    loadedBusinessAreas.map((area) => {
      const existingOption: { ids: string[]; name: string } | undefined = options.find(
        (option) => option.name.toLocaleLowerCase() === area.name.toLocaleLowerCase(),
      );

      if (existingOption) {
        existingOption.ids.push(area.id);
        options = options.filter((option) => option.name.toLowerCase() !== area.name.toLowerCase());
        options.push({
          ids: [...existingOption.ids, area.id],
          name: area.name,
        });
      } else options.push({ ids: [area.id], name: area.name });
    });
    return options;
  }, [loadedBusinessAreas]);

  /**
   * Callback method to copy a project
   * @param projectId id of the project to copy
   */
  const copyProject = useCallback(
    (projectId: string): void => {
      generateProjectCopy(axios, projectId).then(
        (generatedProject) =>
          generatedProject && navigate('/project/edit', { state: { project: generatedProject } }),
      );
    },
    [axios, navigate],
  );

  //Hook to convert projects into tableRows after all projects and customers are loaded
  const rows: TableRow[] = useMemo((): TableRow[] => {
    if (selectedBusinessAreas.ids.length === 0 && selectedProjectStates.length === 0)
      return convertProjectsIntoTableEntries(
        loadedProjects,
        loadedCustomers,
        loadedCustomerLocations,
        loadedCustomerUsers,
        loadedOrders,
        navigate,
        downloadDescription,
        copyProject,
      );

    let filteredProjects = [...loadedProjects];
    if (selectedProjectStates.length > 0) {
      filteredProjects = loadedProjects.filter((project) =>
        selectedProjectStates.includes(project.projectState),
      );
    }
    if (selectedBusinessAreas.ids.length > 0) {
      filteredProjects = filteredProjects.filter((project) => {
        const order: Order | undefined = loadedOrders.find((order) => order.id === project.orderId);
        if (!order) return false;
        else return selectedBusinessAreas.ids.includes(order.businessAreaId);
      });
    }
    return convertProjectsIntoTableEntries(
      filteredProjects,
      loadedCustomers,
      loadedCustomerLocations,
      loadedCustomerUsers,
      loadedOrders,
      navigate,
      downloadDescription,
      copyProject,
    );
  }, [
    downloadDescription,
    copyProject,
    loadedCustomerLocations,
    loadedCustomerUsers,
    loadedCustomers,
    loadedOrders,
    loadedProjects,
    navigate,
    selectedBusinessAreas,
    selectedProjectStates,
  ]);
  //Hook to check if a filter for the current page is present

  useEffect(() => {
    const existingFilter = JSON.parse(localStorage.getItem('buttonFilter') || '{}');
    if (!existingFilter?.path || existingFilter.path !== '/project')
      localStorage.removeItem('buttonFilter');
    else {
      setSelectedBusinessAreas(
        businessAreaOptions.find((option) => option.name === existingFilter.area) ||
          selectedBusinessAreas,
      );
      setSelectedProjectStates(existingFilter?.states);
    }
  }, [businessAreaOptions, selectedBusinessAreas]);

  /**
   * Updates the selected config and add it to the localSorage
   * @param area the selected area
   */
  const handleAreaSelect = (area: { ids: string[]; name: string }) => {
    const existingFilter: { path: string; states: ProjectState[]; area: string } | false =
      JSON.parse(localStorage.getItem('buttonFilter') || 'false');

    if (existingFilter && (!existingFilter?.path || existingFilter.path !== '/project'))
      localStorage.removeItem('buttonFilter');
    else {
      const states: ProjectState[] =
        existingFilter && existingFilter.states ? existingFilter.states : selectedProjectStates;
      localStorage.setItem(
        'buttonFilter',
        JSON.stringify({
          path: '/project',
          area: area.name,
          states: states,
        }),
      );
    }
    setSelectedBusinessAreas(
      selectedBusinessAreas.name === area.name
        ? { ids: [], name: '' }
        : { ids: area.ids, name: area.name },
    );
  };
  /**
   * Updates the selected config and add it to the localSorage
   * @param state the selected projectState
   */
  const handleStateSelect = (state: ProjectState): void => {
    const existingFilter: {
      path: string;
      area: string;
      states: ProjectState[];
    } = JSON.parse(localStorage.getItem('buttonFilter') || 'false');

    localStorage.setItem(
      'buttonFilter',
      JSON.stringify({
        path: '/project',
        area: existingFilter?.area || selectedBusinessAreas.name,
        states: addOrRemove(selectedProjectStates, state),
      }),
    );
    setSelectedProjectStates(addOrRemove(selectedProjectStates, state));
  };
  return (
    <>
      <TopBar title={t('pages.project.overview.topBarHeadline')} onSearch={setFilterValue}>
        <IconComponent type={IconType.ADD} onClick={() => navigate('/project/create')} />
      </TopBar>
      <Box>
        <div className="project-overview__filter-wrapper">
          <p>{t('pages.project.overview.state')}</p>
          <div className="three-columns">
            {Object.values(ProjectState).map((state) => (
              <Button
                value={t(`pages.project.overview.projectState.${state}`)}
                active={selectedProjectStates.includes(state)}
                onClick={() => handleStateSelect(state)}
              />
            ))}
          </div>
          <p>{t('pages.project.overview.businessArea')}</p>
          <div className="three-columns">
            {businessAreaOptions.map((area) => (
              <Button
                value={area.name}
                active={selectedBusinessAreas.name.toLowerCase() === area.name.toLowerCase()}
                onClick={() => handleAreaSelect(area)}
              />
            ))}
          </div>
        </div>
      </Box>
      <Box>
        <Table
          tableConfig={user.tableConfig?.get(TableKey.PROJECT_OVERVIEW)}
          updateTableConfig={(updatedHeader) => {
            updateSchambeckUserTableConfig(
              axios,
              user,
              TableKey.PROJECT_OVERVIEW,
              updatedHeader,
            ).then((updatedUser) => updatedUser && updateContextUser(updatedUser));
          }}
          filterValue={filterValue}
          rows={rows}
          header={
            t('pages.project.overview.tableHeader', {
              returnObjects: true,
            }) as TableHeader[]
          }
        />
      </Box>
    </>
  );
};
